// Subscribers admin: paged list with source + status filters.
// Rows link to /subscribers/:id for the per-subscriber detail view.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { signupPagesApi, subscribersApi } from '../lib/api';
import type {
  SignupPageListItem,
  Subscriber,
  SubscriberStatus,
} from '@shared/types';

const PAGE_SIZE = 50;

const STATUS_LABELS: Record<SubscriberStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  blocked: 'Blocked',
};

export default function SubscribersPage() {
  const [source, setSource] = useState<string>('');
  const [status, setStatus] = useState<SubscriberStatus | ''>('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{
    subscribers: Subscriber[];
    total: number;
  } | null>(null);
  const [pages, setPages] = useState<SignupPageListItem[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // signup_pages now includes the 'homepage' row (migration 0006), so
  // we don't need to prepend it as a fixed option — just sort + dedupe.
  const sourceOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const slug of pages.map((p) => p.slug).sort()) {
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
    return out;
  }, [pages]);

  useEffect(() => {
    void signupPagesApi
      .list()
      .then(({ pages }) => setPages(pages))
      .catch(() => setPages([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    subscribersApi
      .list({
        source: source || undefined,
        status: status || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((res) => setData({ subscribers: res.subscribers, total: res.total }))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [source, status, page]);

  function changeFilter(setter: (v: string) => void, value: string) {
    setter(value);
    setPage(1);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <Link
          to="/signups"
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Signup pages
        </Link>

        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl text-slate-900">Subscribers</h1>
            <p className="text-sm text-slate-500 mt-1">
              {data ? `${data.total} total` : 'Loading…'}
            </p>
          </div>
        </header>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Source
            </label>
            <select
              value={source}
              onChange={(e) => changeFilter(setSource, e.target.value)}
              className="input py-1.5 text-sm"
            >
              <option value="">Any</option>
              {sourceOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => changeFilter((v) => setStatus(v as SubscriberStatus | ''), e.target.value)}
              className="input py-1.5 text-sm"
            >
              <option value="">Any</option>
              {(Object.keys(STATUS_LABELS) as SubscriberStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </div>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {loading && data === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !data || data.subscribers.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center text-sm text-slate-500">
            No subscribers match these filters yet.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
              {data.subscribers.map((s) => {
                const fullName = [s.first_name ?? '', s.last_name ?? ''].join(' ').trim();
                return (
                  <li key={s.id}>
                    <Link to={`/subscribers/${s.id}`} className="block px-4 py-3 active:bg-slate-50">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {fullName || s.email}
                        </span>
                        <span className="text-[11px] text-slate-500 flex-shrink-0">
                          {STATUS_LABELS[s.status]}
                        </span>
                      </div>
                      {fullName && (
                        <div className="text-xs text-slate-500 truncate mt-0.5">{s.email}</div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
                        <code className="bg-slate-100 px-1 py-0.5 rounded">{s.source_slug}</code>
                        <span>·</span>
                        <span>{new Date(s.created_at).toLocaleDateString()}</span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">Email</th>
                    <th className="text-left px-4 py-2 font-medium">Source</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Signed up</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.subscribers.map((s) => {
                    const fullName = [s.first_name ?? '', s.last_name ?? '']
                      .join(' ')
                      .trim();
                    return (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-slate-900">
                          <Link to={`/subscribers/${s.id}`} className="hover:underline">
                            {fullName || <span className="text-slate-400">—</span>}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-slate-900">
                          <Link to={`/subscribers/${s.id}`} className="hover:underline">
                            {s.email}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                          <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{s.source_slug}</code>
                        </td>
                        <td className="px-4 py-2 text-slate-600">{STATUS_LABELS[s.status]}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">
                          {new Date(s.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4 text-xs text-slate-600">
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
